<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';
import api from 'dashboard/api/aceleres/integrations';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WhatsappContractDialog from './WhatsappContractDialog.vue';
const props = defineProps({
  state: { type: Object, required: true },
  highlightId: { type: [String, Number], default: null },
});
const emit = defineEmits(['refresh']);
const people = computed(() => props.state.people || []);
const draft = reactive({});
const busy = ref(false);
const error = ref('');
const errorRequestId = ref(null);
const search = ref('');
const profile = ref('');
const historyOpen = ref(false);
const history = ref([]);
const contractOpen = ref(false);
const rejectionDialog = ref(null);
const rejectionRequest = ref(null);
const rejectionReason = ref('');
const closeRejection = () => {
  if (busy.value) {
    nextTick(() => rejectionDialog.value?.open());
    return;
  }
  if (!rejectionRequest.value) return;
  rejectionRequest.value = null;
  rejectionDialog.value?.close();
};
const profiles = computed(() => [...new Set(people.value.map(p => p.profile))]);
const filteredPeople = computed(() =>
  people.value.filter(
    p =>
      p.name
        .toLocaleLowerCase('pt-BR')
        .includes(search.value.toLocaleLowerCase('pt-BR')) &&
      (!profile.value || p.profile === profile.value)
  )
);
const changed = computed(() =>
  people.value.filter(p => Number(draft[p.id] ?? p.limit) !== Number(p.limit))
);
const distributedDraft = computed(() =>
  people.value.reduce((sum, p) => sum + Number(draft[p.id] ?? p.limit), 0)
);
const freeDraft = computed(
  () => props.state.contracted - distributedDraft.value
);
const pending = computed(() => props.state.requests || []);
const requestFor = person =>
  pending.value.find(r => Number(r.user_id) === person.id);
const reset = () => {
  Object.keys(draft).forEach(k => delete draft[k]);
  error.value = '';
  errorRequestId.value = null;
};
watch(people, (next, previous = []) => {
  const old = new Map(previous.map(p => [p.id, Number(p.limit)]));
  next.forEach(p => {
    if (
      Number(draft[p.id]) === Number(p.limit) ||
      (old.has(p.id) && old.get(p.id) !== Number(p.limit))
    ) {
      delete draft[p.id];
    }
  });
});
const adjust = (person, delta) => {
  const value = Number(draft[person.id] ?? person.limit) + delta;
  if (
    busy.value ||
    value < person.used ||
    value < 0 ||
    (delta > 0 && freeDraft.value <= 0)
  )
    return;
  draft[person.id] = value;
};
const save = async () => {
  if (!changed.value.length || busy.value || freeDraft.value < 0) return;
  busy.value = true;
  error.value = '';
  errorRequestId.value = null;
  try {
    emit(
      'refresh',
      await api.distributeKeys(
        changed.value.map(p => ({ id: p.id, limit: Number(draft[p.id]) }))
      )
    );
    reset();
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
const decide = async (request, decision, reason = '') => {
  if (busy.value) return;
  errorRequestId.value = null;
  if (decision === 'rejected' && rejectionRequest.value?.id !== request.id) {
    rejectionRequest.value = request;
    rejectionReason.value = '';
    error.value = '';
    await nextTick();
    rejectionDialog.value?.open();
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    emit('refresh', await api.decideKeyRequest(request.id, decision, reason));
    busy.value = false;
    closeRejection();
  } catch (e) {
    error.value = e.message;
    errorRequestId.value = request.id;
  } finally {
    busy.value = false;
  }
};
const confirmRejection = () => {
  if (rejectionRequest.value) {
    decide(rejectionRequest.value, 'rejected', rejectionReason.value);
  }
};
const showHistory = async () => {
  historyOpen.value = true;
  error.value = '';
  errorRequestId.value = null;
  try {
    history.value = (await api.getKeyHistory()).events || [];
  } catch (e) {
    error.value = e.message;
  }
};
const formatDate = value => new Date(value).toLocaleString('pt-BR');
const actionLabel = action =>
  ({
    distribution: 'Distribuição alterada',
    request: 'Pedido enviado',
    request_updated: 'Pedido atualizado',
    approved: 'Pedido aprovado',
    rejected: 'Pedido recusado',
    cancel: 'Pedido cancelado',
    consolidation: 'Distribuição consolidada',
    contract_consolidation: 'Contrato consolidado',
    default_assignment: 'Chave padrão',
    reassigned: 'Chave redistribuída',
    key_released: 'Reserva de chave liberada',
    forwarded_to_zoraxy: 'Encaminhado à Zoraxy',
    contract_requested: 'Alteração contratual solicitada',
    contract_request_updated: 'Pedido contratual atualizado',
    contract_approved: 'Contrato aprovado',
    contract_rejected: 'Contrato recusado',
    contract_cancelled: 'Pedido contratual cancelado',
    contract_reduction: 'Redução contratual',
  })[action] || action;
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="integracoes-page__panel integracoes-keys rounded-2xl border border-n-weak bg-n-solid-1 p-[18px] text-[13px] md:min-h-[calc(100vh-180px)]"
  >
    <header class="flex items-start justify-between gap-4">
      <div>
        <p
          class="!mb-0 !text-[10px] !leading-4 font-semibold tracking-widest text-n-slate-11"
        >
          GESTÃO DE LICENÇAS · WHATSAPP
        </p>
        <h2 class="integracoes-keys__title mt-1 text-base font-semibold">
          Chaves de instância
        </h2>
        <p class="mt-1 !mb-0 !text-[12px] !leading-5 text-n-slate-11">
          Todas as pessoas da empresa aparecem aqui com 1 chave por padrão, se
          houver saldo. Ajuste no − / + e salve.
        </p>
      </div>
      <button
        class="integracoes-keys__history !h-[34px] rounded-lg !border !border-solid !border-[#E4E6EA] !px-3 !py-0 !text-[13px]"
        @click="showHistory"
      >
        Histórico
      </button>
    </header>
    <div
      class="mt-5 grid grid-cols-2 gap-4 rounded-xl border border-[#ECEEF1] bg-[#F8F9FA] p-4 text-[#202428] md:grid-cols-4"
    >
      <div>
        <p class="!mb-0 !text-[11px] text-[#57606A]">
          Contratadas pela empresa
        </p>
        <div class="mt-2 flex flex-wrap items-center gap-2">
          <strong class="text-2xl">{{ state.contracted }}</strong
          ><button
            class="integracoes-keys__contract !h-[30px] rounded-lg !border !border-solid !border-[#E4E6EA] bg-white !px-3 !py-0 !text-[11px]"
            @click="contractOpen = true"
          >
            Alterar quantidade
          </button>
        </div>
      </div>
      <div>
        <p class="!mb-0 !text-[11px] text-[#57606A]">Distribuídas a pessoas</p>
        <strong class="mt-2 block text-2xl">{{ state.distributed }}</strong>
      </div>
      <div>
        <p class="!mb-0 !text-[11px] text-[#57606A]">Em uso</p>
        <strong class="mt-2 block text-2xl">{{ state.used }}</strong>
      </div>
      <div>
        <p class="!mb-0 !text-[11px] text-[#57606A]">Livres para distribuir</p>
        <strong
          class="mt-2 block text-2xl"
          :class="state.free === 0 ? 'text-[#9A4B00]' : ''"
          >{{ state.free }}</strong
        >
      </div>
    </div>
    <div
      v-if="state.contract_request"
      class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#C9DDF2] bg-[#F2F7FD] p-4 text-[13px] text-[#202428]"
    >
      <span
        ><strong>Pedido enviado:</strong> de
        {{ state.contract_request.previous_total }} para
        {{ state.contract_request.target_total }} chaves · aguardando liberação
        da equipe Zoraxy.</span
      ><button
        class="font-semibold text-[#15803D]"
        @click="contractOpen = true"
      >
        Editar pedido
      </button>
    </div>
    <div class="mb-4 mt-6 flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-sm font-semibold">Pessoas da empresa</h3>
      <div
        class="integracoes-keys__filters flex flex-wrap items-center gap-2 sm:flex-nowrap"
      >
        <input
          v-model="search"
          aria-label="Buscar pessoa"
          placeholder="Buscar pessoa"
          class="integracoes-keys__search !mb-0 !h-[34px] !w-[220px] max-w-full rounded-lg !border !border-solid !border-[#E4E6EA] bg-n-solid-1 !px-3 !py-0 !text-[12px] !outline-none"
        /><select
          v-model="profile"
          aria-label="Filtrar perfil"
          class="integracoes-keys__profile !mb-0 !h-[34px] !w-auto max-w-full rounded-lg !border !border-solid !border-[#E4E6EA] bg-n-solid-1 !pl-3 !pr-7 !py-0 !text-[12px] !outline-none"
        >
          <option value="">Todos os perfis</option>
          <option v-for="item in profiles" :key="item" :value="item">
            {{ item }}
          </option>
        </select>
      </div>
    </div>
    <div class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="integracoes-keys__table w-full text-left !text-[13px]">
        <thead
          class="bg-[#F8F9FA] text-[10px] uppercase tracking-wide text-n-slate-11"
        >
          <tr>
            <th class="w-[31%] px-[14px] py-[10px]">Pessoa · perfil</th>
            <th class="w-[19%] px-[14px] py-[10px]">Chaves</th>
            <th class="w-[25%] px-[14px] py-[10px]">Em uso</th>
            <th class="w-[25%] px-[14px] py-[10px]" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="person in filteredPeople"
            :key="person.id"
            class="border-t border-n-weak"
            :class="
              person.missing_default && person.limit === 0 ? 'bg-[#FFF8EF]' : ''
            "
          >
            <td class="px-[14px] py-[10px]">
              <strong>{{ person.name }}</strong
              ><small class="block text-[12px] text-n-slate-11">{{
                person.profile
              }}</small>
            </td>
            <td class="px-[14px] py-[10px]">
              <div
                class="integracoes-keys__quantity flex items-center gap-2"
                role="group"
                :aria-label="`Alterar quantidade de ${person.name}`"
              >
                <button
                  :disabled="
                    busy ||
                    Number(draft[person.id] ?? person.limit) <= person.used
                  "
                  title="Chaves em uso não podem ser retiradas"
                  class="integracoes-keys__decrease !h-[27px] !w-[27px] rounded-md !border !border-solid !border-[#E4E6EA] !p-0 !text-[13px] disabled:opacity-40"
                  @click="adjust(person, -1)"
                >
                  −</button
                ><strong>{{ draft[person.id] ?? person.limit }}</strong
                ><button
                  :disabled="busy || freeDraft <= 0"
                  class="integracoes-keys__increase !h-[27px] !w-[27px] rounded-md !border !border-solid !border-[#E4E6EA] !p-0 !text-[13px] disabled:opacity-40"
                  @click="adjust(person, 1)"
                >
                  +
                </button>
              </div>
            </td>
            <td class="px-[14px] py-[10px] text-n-slate-11">
              {{ person.used }} de {{ draft[person.id] ?? person.limit }} em uso
            </td>
            <td class="px-[14px] py-[10px] text-right">
              <div
                v-if="requestFor(person)"
                class="integracoes-keys__request flex items-center justify-end gap-3 whitespace-nowrap"
              >
                <span
                  class="integracoes-keys__request-label text-[12px] leading-5"
                  >Pediu +{{ requestFor(person).quantity }}</span
                ><span
                  v-if="requestFor(person).stage === 'zoraxy'"
                  class="text-n-slate-11"
                  >Aguardando a Zoraxy</span
                ><template v-else
                  ><button
                    :disabled="busy"
                    class="integracoes-keys__reject !h-[30px] !px-0 !py-0 !text-[12px] !leading-5 text-[#B42318] disabled:opacity-40"
                    @click="decide(requestFor(person), 'rejected')"
                  >
                    Recusar</button
                  ><button
                    :disabled="busy"
                    class="integracoes-keys__approve !h-[30px] !px-0 !py-0 !text-[12px] !leading-5 text-[#15803D] disabled:opacity-40"
                    @click="decide(requestFor(person), 'approved')"
                  >
                    Aprovar
                  </button></template
                >
              </div>
              <span
                v-else-if="person.missing_default && person.limit === 0"
                class="text-xs font-semibold text-[#9A4B00]"
                >Novo usuário · sem chave livre</span
              >
              <p
                v-if="error && requestFor(person)?.id === errorRequestId"
                role="alert"
                class="integracoes-keys__request-error mt-2 !mb-0 !text-[12px] !leading-5 text-n-ruby-11"
              >
                {{ error }}
              </p>
            </td>
          </tr>
          <tr v-if="!filteredPeople.length">
            <td colspan="4" class="p-4 text-n-slate-11">
              Nenhuma pessoa encontrada.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="changed.length"
      class="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#202428] p-4 text-sm text-white"
    >
      <span
        >{{ changed.length }}
        {{ changed.length === 1 ? 'alteração' : 'alterações' }} ·
        {{ freeDraft }} livres após salvar</span
      >
      <div class="flex gap-2">
        <button :disabled="busy" class="px-3 py-2" @click="reset">
          Cancelar</button
        ><button
          :disabled="busy || freeDraft < 0"
          class="rounded-lg bg-[#16A34A] px-4 py-2 font-semibold disabled:opacity-40"
          @click="save"
        >
          Salvar
        </button>
      </div>
    </div>
    <p
      v-if="error && errorRequestId === null"
      role="alert"
      class="mt-3 text-sm text-n-ruby-11"
    >
      {{ error }}
    </p>
    <div class="mt-5 grid min-w-0 gap-4 md:grid-cols-2">
      <section class="min-w-0 rounded-xl border border-n-weak p-4">
        <h3 class="text-sm font-semibold">Regras de uso</h3>
        <label class="mt-3 flex items-center justify-between gap-3 text-sm"
          ><span
            >Bloquear conexão acima do limite<small
              class="mt-1 block text-n-slate-11"
              >Vale para a venda direto pelo site.</small
            ></span
          ><input
            aria-label="Bloquear conexão acima do limite"
            type="checkbox"
            :checked="true"
            :disabled="true"
        /></label>
      </section>
      <section class="min-w-0 rounded-xl border border-n-weak p-4">
        <h3 class="text-sm font-semibold">
          Pedidos de chave
          <span class="font-normal text-n-slate-11"
            >{{ pending.length }} pendentes</span
          >
        </h3>
        <p v-if="!pending.length" class="mt-3 text-sm text-n-slate-11">
          Nenhum pedido pendente.
        </p>
        <div
          v-for="request in pending"
          :key="request.id"
          class="mt-3 min-w-0 border-t border-n-weak pt-3 text-sm [overflow-wrap:anywhere]"
          :class="
            String(highlightId) === String(request.id) ? 'bg-[#FFF8EF]' : ''
          "
        >
          <strong
            >{{ people.find(p => p.id === Number(request.user_id))?.name }} ·
            +{{ request.quantity }}</strong
          >
          <p
            data-key-request-reason
            class="mt-1 whitespace-pre-wrap text-n-slate-11 [overflow-wrap:anywhere]"
          >
            {{ request.reason || 'Sem motivo informado' }} ·
            {{ formatDate(request.created_at) }}
          </p>
          <p v-if="request.stage === 'zoraxy'" class="mt-2 text-n-slate-11">
            Aguardando liberação da equipe Zoraxy.
          </p>
          <div v-else class="mt-2 flex gap-4">
            <button
              :disabled="busy"
              class="text-[#B42318]"
              @click="decide(request, 'rejected')"
            >
              Recusar</button
            ><button
              :disabled="busy"
              class="font-semibold text-[#15803D]"
              @click="decide(request, 'approved')"
            >
              Aprovar
            </button>
          </div>
        </div>
      </section>
    </div>
    <Dialog
      ref="rejectionDialog"
      title="Recusar pedido de chaves"
      description="O solicitante será notificado da recusa."
      confirm-button-label="Recusar pedido"
      cancel-button-label="Cancelar"
      :is-loading="busy"
      @confirm="confirmRejection"
      @close="closeRejection"
    >
      <label class="block text-sm">
        Motivo da recusa (opcional)
        <textarea
          v-model="rejectionReason"
          maxlength="2000"
          :disabled="busy"
          class="mt-2 w-full rounded-lg border border-n-weak bg-n-solid-1 p-3 text-sm"
          rows="3"
        />
      </label>
      <p
        v-if="error"
        role="alert"
        class="text-sm text-n-ruby-11 [overflow-wrap:anywhere]"
      >
        {{ error }}
      </p>
    </Dialog>
    <WhatsappContractDialog
      v-if="contractOpen"
      :state="state"
      @close="contractOpen = false"
      @refresh="next => emit('refresh', next)"
    />
    <Teleport to="body"
      ><div
        v-if="historyOpen"
        class="fixed inset-0 z-[110] flex items-center justify-center bg-modal-backdrop-dark p-4"
        @click.self="historyOpen = false"
      >
        <section
          class="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-[18px] bg-n-solid-1 p-6 shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-label="Histórico de chaves"
        >
          <header class="flex justify-between">
            <h2 class="text-lg font-semibold">Histórico</h2>
            <button @click="historyOpen = false">Fechar</button>
          </header>
          <p v-if="error" role="alert" class="mt-3 text-n-ruby-11">
            {{ error }}
          </p>
          <p v-if="!history.length" class="mt-4 text-sm text-n-slate-11">
            Nenhum registro.
          </p>
          <div
            v-for="event in history"
            :key="event.id"
            class="mt-3 min-w-0 border-t border-n-weak pt-3 text-sm [overflow-wrap:anywhere]"
          >
            <strong
              >{{ actionLabel(event.action) }} ·
              {{ event.person_name || 'Empresa' }}</strong
            >
            <p class="text-n-slate-11">
              {{ event.before_limit }} → {{ event.after_limit }} ·
              {{ event.actor_name || 'Sistema' }} ·
              {{ formatDate(event.created_at) }}
            </p>
          </div>
        </section>
      </div></Teleport
    >
  </section>
</template>
